import React, { useEffect, useRef, useState } from "react";

import { AlertCircle, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import useBreakpoint from "use-breakpoint";

import { getReminders } from "@/apis";
import { MarkdownContent } from "@/components/Typewriter";
import { BREAKPOINTS } from "@/constants/defaultValue";
import { getSMBaseUrl } from "@/lib/utils";
import useChatStore from "@/stores/useChatStore";
import useGlobalStore from "@/stores/useGlobalStore";

import { formatDateDisplay, formatDateForGrouping, formatTime } from "@/modules/chat-app/chatApp.utils";
import TimelineSeparator from "@/modules/chat-app/components/TimelineSperator";

import "../style.css";

const Reminder = () => {
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const business = useGlobalStore((state) => state.business);

  const [error, setError] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [loadedCount, setLoadedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [messageGroups, setMessageGroups] = useState([]);
  const [page, setPage] = useState(0);
  const [totalMessages, setTotalMessages] = useState(0);
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const { breakpoint } = useBreakpoint(BREAKPOINTS);

  const isMobile = ["xs", "sm"].includes(breakpoint);

  const fetchReminders = async () => {
    if (!business) return;

    try {
      setLoading(true);
      setError(null);

      const response = await getReminders({ business, page: 0, size: 30, isMobile });

      if (response?.items && response?.total !== undefined) {
        const apiData = response.items;
        const total = response.total;

        const transformedData = transformApiDataToMessageGroups(apiData);

        setTotalMessages(total);
        setLoadedCount(apiData.length);
        setMessageGroups(transformedData);

        if (isInitialLoad) {
          setIsInitialLoad(false);
        }
      }

      if (!response.items?.length) {
        navigate("/");
      }
    } catch (err) {
      console.error("Failed to fetch reminders:", err);
      setError("Không thể tải dữ liệu. Vui lòng thử lại.");
    } finally {
      setLoading(false);
    }
  };

  const transformApiDataToMessageGroups = (apiData) => {
    if (!Array.isArray(apiData) || apiData.length === 0) {
      return [];
    }

    const groupedByDate = {};

    apiData.forEach((item) => {
      const createdAt = item.createdAt ? new Date(item.createdAt) : new Date();
      const dateKey = formatDateForGrouping(createdAt);

      if (!groupedByDate[dateKey]) {
        groupedByDate[dateKey] = {
          date: formatDateDisplay(createdAt),
          time: formatTime(createdAt),
          messages: [],
        };
      }
      const message = { ...item, time: formatTime(createdAt), id: item.reminderId, title: item.title || "Thông báo hệ thống" };
      groupedByDate[dateKey].messages.push(message);
    });

    return Object.values(groupedByDate).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  };

  useEffect(() => {
    fetchReminders(0, true);
  }, [business]);

  const handleRefresh = () => {
    setPage(0);
    setLoadedCount(0);
    setIsInitialLoad(true);
    fetchReminders(0, true);
  };


  const handleReminderClick = (reminderId) => {
    navigate(`/reminder/${reminderId}`);
  }

  if (loading && messageGroups.length === 0) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-500 mr-2" />
          <span className="text-gray-600">Đang tải dữ liệu...</span>
        </div>
      </div>
    );
  }

  if (error && messageGroups.length === 0) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className="flex flex-col items-center justify-center py-8 space-y-4">
          <AlertCircle className="w-8 h-8 text-red-500" />
          <p className="text-red-600 text-center">{error}</p>
          <button onClick={handleRefresh} className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
            <RefreshCw className="w-4 h-4" />
            Thử lại
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[820px] mx-auto p-2 pl-6" ref={containerRef}>
      {messageGroups.map((group, groupIndex) => (
        <div key={`${groupIndex}-${group.date}`}>
          {group.messages.length > 0 && <TimelineSeparator date={group.date} time={group.time} />}

          <div className="space-y-4 mb-8">
            {group.messages.map((message, index) => (
              <div
                key={message.id + index || index}
                className="p-4 shadow-sm relative bg-[#208DF10D] rounded-lg transition-all duration-200 hover:shadow-md group cursor-pointer"
                onMouseEnter={() => setHoveredMessageId(message.id)}
                onMouseLeave={() => setHoveredMessageId(null)}
                onClick={() => handleReminderClick(message.id)}
              >
                <div className="absolute top-4 right-4 flex items-center gap-2">
                  <span className={`text-xs text-gray-400`}>{message.time}</span>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-1 space-y-6 w-full overflow-x-scroll">
                    <h3 className={`font-bold text-gray-900 leading-relaxed pr-12 ${isMobile ? "text-base" : "text-xl"}`}>{message.title}</h3>

                    {message.subtitle && <p className="text-sm text-gray-600 leading-relaxed">{message.subtitle}</p>}

                    <MarkdownContent content={message.content} className="text-gray-700 leading-relaxed text-sm reminder-markdown" />

                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

    </div>
  );
};

export default React.memo(Reminder);
